import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, X, ScanLine, Wallet } from "lucide-react";
import { FundleLogo } from "./Brand";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { fmtPoints } from "../lib/api";

const NAV_LINKS = [
    { to: "/category/all", label: "All Deals" },
    { to: "/category/Apparel", label: "Apparel" },
    { to: "/category/Jewellery", label: "Jewellery" },
    { to: "/category/Food%20%26%20Beverage", label: "Food" },
    { to: "/category/Travel", label: "Travel" },
    { to: "/category/Online%20Shopping", label: "Online" },
    { to: "/category/Hotels", label: "Hotels" },
    { to: "/category/Electronics", label: "Electronics" },
];

export default function TopNav() {
    const { customer, wallet } = useAuth();
    const { count } = useCart();
    const navigate = useNavigate();
    const loc = useLocation();
    const [q, setQ] = useState("");
    const [openMenu, setOpenMenu] = useState(false);

    useEffect(() => setOpenMenu(false), [loc.pathname]);

    const onSearch = (e) => {
        e.preventDefault();
        if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    };

    return (
        <>
            <div className="bg-[#0b1437] text-white text-[11px] py-1.5 px-4 text-center tracking-wide">
                Delivered in 10 seconds · 270+ brands · <span className="text-[#c9a959]">खुशियाँ भेजो</span>
            </div>
            <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[rgba(11,20,55,0.08)]">
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
                    <button onClick={() => setOpenMenu(true)} className="lg:hidden p-2 -ml-2 text-[#0b1437]" aria-label="Menu">
                        <Menu size={22} />
                    </button>
                    <Link to="/" className="shrink-0" data-testid="nav-logo">
                        <FundleLogo className="h-9" />
                    </Link>

                    <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-2xl mx-4">
                        <div className="flex items-center gap-2 w-full bg-[#f9fafb] border border-[rgba(11,20,55,0.10)] rounded-full px-4 py-2.5 focus-within:border-[#0b1437] focus-within:bg-white focus-within:ring-4 focus-within:ring-[rgba(11,20,55,0.06)] transition-all">
                            <Search size={16} className="text-[#6b7280]" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                data-testid="topnav-search-input"
                                placeholder="Search brands · gift cards · deals"
                                className="flex-1 bg-transparent outline-none text-sm placeholder-[#9ca3af] text-[#0b1437]"
                            />
                            {q && (
                                <button type="button" onClick={() => setQ("")} className="text-[#9ca3af] hover:text-[#0b1437]">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </form>

                    <div className="flex items-center gap-1 md:gap-3 ml-auto">
                        <Link to="/account/scan" className="hidden md:inline-flex items-center gap-1.5 text-[#0b1437] hover:text-[#c9a959] text-sm px-3 py-2" data-testid="nav-scan-link">
                            <ScanLine size={16} /> Scan
                        </Link>
                        {customer && (
                            <Link to="/account/wallet" className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#c9a959]/10 border border-[#c9a959]/40 text-[#0b1437] text-xs font-semibold" data-testid="nav-wallet-chip">
                                <Wallet size={13} className="text-[#c9a959]" />{fmtPoints(wallet?.points_balance || 0)}
                            </Link>
                        )}
                        <Link to="/cart" data-testid="nav-cart-btn" className="relative p-2 text-[#0b1437] hover:text-[#c9a959]">
                            <ShoppingCart size={20} />
                            {count > 0 && (
                                <span data-testid="cart-badge-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#e11d48] text-white text-[10px] font-bold flex items-center justify-center px-1">
                                    {count}
                                </span>
                            )}
                        </Link>
                        {customer ? (
                            <Link to="/account" data-testid="nav-account-btn" className="flex items-center gap-2 p-1 pr-3 rounded-full hover:bg-[#f9fafb]">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#c9a959] to-[#a07e2c] text-white flex items-center justify-center font-bold text-sm">
                                    {(customer.name || "F")[0].toUpperCase()}
                                </div>
                                <span className="hidden md:inline text-sm text-[#0b1437] max-w-[120px] truncate">
                                    {customer.name?.split(" ")[0] || customer.mobile.slice(-4)}
                                </span>
                            </Link>
                        ) : (
                            <Link to="/login" data-testid="nav-login-btn" className="fx-btn-primary !py-2 !px-4 !text-sm">
                                <User size={14} /> Sign in
                            </Link>
                        )}
                    </div>
                </div>

                <form onSubmit={onSearch} className="md:hidden px-4 pb-3">
                    <div className="flex items-center gap-2 w-full bg-[#f9fafb] border border-[rgba(11,20,55,0.10)] rounded-full px-4 py-2">
                        <Search size={15} className="text-[#6b7280]" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="topnav-mobile-search-input" placeholder="Search brands & deals" className="flex-1 bg-transparent outline-none text-sm placeholder-[#9ca3af] text-[#0b1437]" />
                    </div>
                </form>

                <nav className="hidden lg:flex max-w-[1400px] mx-auto px-6 h-11 items-center gap-1 border-t border-[rgba(11,20,55,0.05)]">
                    {NAV_LINKS.map((l) => (
                        <Link key={l.to} to={l.to} data-testid={`nav-cat-${l.label}`} className="px-3 py-1.5 text-sm text-[#374151] hover:text-[#c9a959] rounded-full transition-colors">
                            {l.label}
                        </Link>
                    ))}
                    <a href="https://wa.me/918072905306?text=Hi" target="_blank" rel="noreferrer" className="ml-auto text-xs text-[#16A34A] flex items-center gap-1 font-semibold">
                        💬 Join on WhatsApp
                    </a>
                </nav>
            </header>

            {openMenu && (
                <div className="fixed inset-0 z-[60] lg:hidden" onClick={() => setOpenMenu(false)}>
                    <div className="absolute inset-0 bg-black/40" />
                    <aside className="absolute top-0 left-0 bottom-0 w-72 bg-white p-5 flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <FundleLogo className="h-9" />
                            <button onClick={() => setOpenMenu(false)} className="text-[#6b7280]"><X size={20} /></button>
                        </div>
                        <nav className="mt-6 space-y-1">
                            {NAV_LINKS.map((l) => (
                                <Link key={l.to} to={l.to} className="block px-3 py-2 rounded-lg text-[#0b1437] hover:bg-[#f9fafb]">
                                    {l.label}
                                </Link>
                            ))}
                        </nav>
                        <div className="mt-auto space-y-2">
                            <Link to="/account/scan" className="fx-btn-ghost w-full !text-sm">
                                <ScanLine size={14} /> Scan a bill
                            </Link>
                            <a href="https://wa.me/918072905306?text=Hi" target="_blank" rel="noreferrer" className="fx-btn-ghost w-full !text-sm">
                                💬 WhatsApp join
                            </a>
                        </div>
                    </aside>
                </div>
            )}
        </>
    );
}
